import { attemptFailureCode, attemptFailureMessage, scoredNotice } from '@/lib/failures'
import { RedTeamTaskIcon } from '@/components/RedTeamTaskIcon'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useBoardData } from '@/components/useBoardData'
import { AdditionalMarker, FailureBreakdown, FailureChip, ScoredChip } from '@/components/JobFailure'
import { JobPanel } from '@/components/JobPanel'
import { ModelLabel } from '@/components/ModelLabel'
import { ExperimentJobs } from '@/components/runs/ExperimentJobs'
import { StopExperimentConfirmation } from '@/components/runs/StopExperimentConfirmation'
import { NodePanel } from '@/components/NodePanel'
import { RunFleet, StepMeter } from '@/components/RunFleet'
import { fmtDateTime } from '@/lib/format'
import { failedAttempts, gradingIdentity, jobStage, runPrograms } from '@/lib/runs'
import { Icon } from '@/components/Icon'
import type { RunDetailPayload, Rollout, TaskInfo, TaskResult } from '@/lib/runs'
import './run-detail.css'

const modelsOf = (jobs: Rollout[]) => new Set(jobs.map(job => job.model).filter((model): model is string => !!model))

/** A finished run's jobs, grouped by task. Same rollout inspector as the active job list. */
function TaskSection({ task, info, selectedJobId, onSelect }: { task: TaskResult; info?: TaskInfo; selectedJobId?: string; onSelect: (jobId: string) => void }) {
  return <section className="section" key={task.task_id}>
    <div className="section-head">
      <div className="task-title">
        <h2><RedTeamTaskIcon task={info ?? task} />{info?.title ?? task.task_id}</h2>
        <code className="muted small">{task.task_id}</code>
      </div>
      <div className="row small muted"><span>{Math.round(task.duration_seconds)}s</span><span>{(task.token_usage.input + task.token_usage.output).toLocaleString()} tokens</span><span className={task.failures ? 'err-text' : ''}>{task.failures} {task.failures === 1 ? 'failure' : 'failures'}</span></div>
    </div>
    {info?.prompt && <details className="task-prompt"><summary>Task prompt</summary><p className="prewrap">{info.prompt}</p></details>}
    {task.rollouts.length ? <div className="task-job-rows">{task.rollouts.map(rollout => {
      const grading = gradingIdentity(rollout.attempt_kind)
      const multiModel = modelsOf(task.rollouts).size > 1
      const stage = jobStage(rollout)
      const failure = failedAttempts(rollout.attempts).at(-1)
      const failureCode = attemptFailureCode(failure)
      const failureMessage = attemptFailureMessage(failure)
      const showHookError = failureCode === 'hook.exit_nonzero' && failure?.diagnostic?.message
      const scored = rollout.attempts.find(attempt => attempt.classification === 'success')
      const reward = scored?.summary?.reward
      const notice = scoredNotice(scored)
      return <button className={`task-job-row${selectedJobId === rollout.job_id ? ' selected' : ''}`} type="button" key={rollout.job_id} onClick={() => onSelect(rollout.job_id)} aria-pressed={selectedJobId === rollout.job_id}>
        <span className={`job-state-dot ${stage}`} aria-hidden="true" />
        <span className="task-job-name">{grading ? <span className="evaluation-label"><Icon name={grading.icon} size={16} />{grading.label}</span> : <>Rollout {rollout.replicate + 1}<small className="muted"> · seed {rollout.seed}{multiModel && rollout.model && <> · {rollout.model}</>}</small></>}</span>
        {failure ? <span className={`task-job-message${showHookError ? ' task-job-hook-error' : ''}`}>
          {showHookError ? <code>{failureCode}</code> : <FailureChip errorClass={failureCode} compact />}
          <small className="muted">{failureMessage}</small><AdditionalMarker attempt={failure} />
        </span>
          : notice ? <span className="task-job-message"><ScoredChip attempt={scored} /><small className="muted">{notice.message || notice.code}</small><AdditionalMarker attempt={scored} /></span>
          : <AdditionalMarker attempt={scored} />}
        {rollout.step ? <StepMeter step={rollout.step} maxSteps={rollout.max_steps} /> : null}
        <span className="task-job-score">{reward == null ? '—' : reward.toFixed(2)}</span>
      </button>
    })}</div> : <p className="empty">No rollout jobs were recorded.</p>}
  </section>
}

/**
 * How far the experiment has got. A failed job is as finished as a completed one —
 * the runner will not touch it again — so it fills its segment too, in red. Counting
 * only successes left a stalled experiment looking like a slow one.
 */
function RunProgress({ done, failed, total }: { done: number; failed: number; total: number }) {
  if (!total) return null
  return <div className="run-progress">
    <div className="run-progress-bar" role="img"
         aria-label={`${done} of ${total} jobs completed, ${failed} failed`}>
      <i className="done" style={{ width: `${(done / total) * 100}%` }} />
      <i className="failed" style={{ width: `${(failed / total) * 100}%` }} />
    </div>
    <p className="muted small">
      <strong>{done}</strong> completed
      {failed > 0 && <> · <strong className="err-text">{failed}</strong> failed</>}
      {` · ${Math.max(0, total - done - failed)} to go`}
    </p>
  </div>
}

export default function RunDetail() {
  const { runId = '' } = useParams()
  const { data: detail, error: loadError, reload: load } = useBoardData<RunDetailPayload>(`/eng/runs/${runId}`)
  const [error, setError] = useState('')
  const [stopping, setStopping] = useState(false)
  const [confirmingStop, setConfirmingStop] = useState<string | null>(null)
  const stopPending = useRef(false)
  const stopButton = useRef<HTMLButtonElement>(null)
  const [taskInfo, setTaskInfo] = useState<TaskInfo[]>([])
  // The open job — or node — lives in the URL, so a link to a failing rollout can
  // be shared. Only one panel is open at a time: they are the same surface.
  const [params, setParams] = useSearchParams()
  const selectedJobId = params.get('job') ?? undefined
  const selectedNodeId = params.get('node') ?? undefined
  const open = useCallback((key: 'job' | 'node', value?: string) => setParams(previous => {
    const next = new URLSearchParams(previous)
    next.delete('job'); next.delete('node')
    if (value) next.set(key, value)
    return next
  }, { replace: true }), [setParams])
  const select = useCallback((jobId?: string) => open('job', jobId), [open])
  const selectNode = useCallback((instanceId?: string) => open('node', instanceId), [open])
  useEffect(() => { if (!detail?.run.active) return; const timer = window.setInterval(() => { if (!document.hidden) load() }, 10000); return () => clearInterval(timer) }, [detail?.run.active, load])
  // Fetch only this run's task IDs at each pinned revision, in bounded batches.
  const catalogGroups = new Map<string, { program: string; dataset_revision: string; tasks: string[] }>()
  if (detail) {
    const config = detail.run.configuration
    for (const taskId of config.tasks) {
      const ref = config.task_references?.[taskId]
      const program = ref?.program ?? config.program
      const revision = ref?.commit ?? config.dataset_revision
      if (!program || !revision) continue
      const key = `${program}:${revision}`
      const group = catalogGroups.get(key) ?? { program, dataset_revision: revision, tasks: [] }
      group.tasks.push(taskId)
      catalogGroups.set(key, group)
    }
  }
  const catalogKey = JSON.stringify([...catalogGroups.values()])
  useEffect(() => {
    const groups: { program: string; dataset_revision: string; tasks: string[] }[] = JSON.parse(catalogKey)
    let cancelled = false
    setTaskInfo([])
    const catalogue = async (group: typeof groups[number]): Promise<TaskInfo[]> => {
      const rows: TaskInfo[] = []
      for (let offset = 0; offset < group.tasks.length && !cancelled; offset += 100) {
        const page = await api.get<{ tasks: TaskInfo[] }>('/eng/runs/tasks', {
          program: group.program, revision: group.dataset_revision,
          tasks: group.tasks.slice(offset, offset + 100).join(','),
        })
        rows.push(...page.tasks)
      }
      return rows
    }
    Promise.all(groups.map(group => catalogue(group).catch(() => [])))
      .then(values => { if (!cancelled) setTaskInfo(values.flat()) })
    return () => { cancelled = true }
  }, [catalogKey])
  const tasks = useMemo(() => new Map(taskInfo.map(task => [task.task_id, task])), [taskInfo])

  async function stop() {
    if (stopPending.current || confirmingStop !== runId) return
    stopPending.current = true
    setStopping(true)
    setError('')
    try {
      await api.post(`/eng/runs/${runId}/stop`)
      setConfirmingStop(null)
      await load(true)
    } catch (e) { setError((e as Error).message) }
    finally { stopPending.current = false; setStopping(false) }
  }
  if ((error || loadError) && !detail) return <div className="note err">{error || loadError}</div>; if (!detail) return <p className="muted">Loading experiment…</p>
  const run = detail.run
  const jobs = detail.tasks.flatMap((task: TaskResult) => task.rollouts)
  const selected = jobs.find(job => job.job_id === selectedJobId)
  const selectedNode = detail.nodes.find(node => node.instance_id === selectedNodeId)
  const done = jobs.filter(job => jobStage(job) === 'completed' || jobStage(job) === 'stopped').length
  const failed = jobs.filter(job => jobStage(job) === 'failed').length
  return <div className="run-detail-page">
    <Link className="backlink" to="/eng/runs">← Experiments</Link>
    <div className="page-head experiment-detail-head">
      <div><div className="experiment-detail-state"><span className={`job-list-status ${run.status.includes('warning') ? 'warning' : run.active ? 'running' : run.status === 'failed' ? 'failed' : 'completed'}`}>{run.display_status.replaceAll('_', ' ')}</span><span>{runPrograms(run.configuration)} · {run.configuration.framework}</span></div>
        <h1>{run.name}</h1>
        <div className="experiment-detail-meta"><ModelLabel model={run.configuration.model} /><span>Started {fmtDateTime(run.created_at)}</span></div>
      </div>
      {run.active && run.origin !== 'cli' && <button ref={stopButton} className="experiment-stop" disabled={stopping} onClick={() => { setError(''); setConfirmingStop(runId) }}>{stopping ? 'Stopping…' : 'Stop experiment'}</button>}
    </div>
    {confirmingStop === runId && <StopExperimentConfirmation busy={stopping} error={error} returnFocus={stopButton.current}
      onCancel={() => setConfirmingStop(null)} onConfirm={stop} />}
    {(error || loadError) && <div className="note err">{error || loadError}</div>}{run.failure_reason && <div className="note err">{run.failure_reason}</div>}
    <section className="experiment-completion" aria-label="Experiment progress">
      <div className="experiment-completion-title"><h2>{done + failed}<span> / {jobs.length} jobs finished</span></h2><span>{detail.tasks.length} {detail.tasks.length === 1 ? 'task' : 'tasks'} <span>·</span> {detail.fleet.reporting}/{detail.fleet.requested} nodes reporting</span></div>
      <RunProgress done={done} failed={failed} total={jobs.length} />
    </section>
    {run.active
      ? <ExperimentJobs key={runId} jobs={jobs} tasks={tasks} selectedJobId={selectedJobId} onSelect={select} />
      : <>
        <FailureBreakdown attempts={jobs.flatMap(job => job.attempts)} />
        {detail.tasks.map(task => <TaskSection key={task.task_id} task={task} info={tasks.get(task.task_id)} selectedJobId={selectedJobId} onSelect={select} />)}
      </>}
    {(run.active || detail.nodes.length > 0) && <RunFleet fleet={detail.fleet} nodes={detail.nodes} jobs={jobs} tasks={tasks}
                                                          selectedNodeId={selectedNodeId} onSelectJob={select} onSelectNode={selectNode} />}
    {selected && <JobPanel runId={runId} runName={run.name} configuration={run.configuration} rollout={selected}
                           task={tasks.get(selected.task_id)} taskRollouts={jobs.filter(job => job.task_id === selected.task_id).length}
                           rubricMap={detail.tasks.find(task => task.task_id === selected.task_id)?.rubric_map}
                           onClose={() => select(undefined)} />}
    {selectedNode && <NodePanel runId={runId} runName={run.name} configuration={run.configuration} node={selectedNode}
                                jobs={jobs} tasks={tasks} onSelectJob={select} onClose={() => selectNode(undefined)} />}
  </div>
}
